import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Phone, FileText, Mic, Clock, GitBranch, CheckSquare } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import HERO_PRODUCT_IMG from '../assets/scribai-hero-cutout.png';

gsap.registerPlugin(ScrollTrigger);

const RECORDINGS = [
  { title: 'Lecture: Cognitive Science', duration: '42:18' },
  { title: 'Team Standup — Aug 6', duration: '12:04' },
  { title: 'Client Call: Q3 Review', duration: '28:51' },
  { title: 'Interview — Product Lead', duration: '35:07' },
];

const USE_CASE_ICONS = [Phone, FileText, Mic, Clock, GitBranch, CheckSquare];

// Real DOM section (not a flat image) so text stays crisp at any resolution/zoom.
// Entrance is scroll-triggered, once, marketing-tier motion per the animate skill's gate:
// occasional/explanatory content on a page, not a frequent UI element — longer, tasteful
// durations are appropriate here. transform+opacity only, strong ease-out curve, staggered
// (30-80ms) so nothing enters as one flat block, and gated behind prefers-reduced-motion.
const SmartSummariesSection = () => {
  const { t } = useLanguage();
  const sectionRef = useRef(null);
  const phoneRef = useRef(null);
  const deviceRef = useRef(null);
  const rowsRef = useRef([]);
  const gridRef = useRef(null);

  const usecaseLabels = [
    t('smartSummaries.useCase1'),
    t('smartSummaries.useCase2'),
    t('smartSummaries.useCase3'),
    t('smartSummaries.useCase4'),
    t('smartSummaries.useCase5'),
    t('smartSummaries.useCase6'),
  ];

  useEffect(() => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const ctx = gsap.context(() => {
      const ease = 'cubic-bezier(0.23, 1, 0.32, 1)';

      if (reduceMotion) {
        gsap.set(
          [
            sectionRef.current.querySelector('.ss-headline'),
            sectionRef.current.querySelector('.ss-subhead'),
            phoneRef.current,
            deviceRef.current,
            ...rowsRef.current,
            ...gridRef.current.children,
          ],
          { opacity: 1, clearProps: 'transform' }
        );
        return;
      }

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 78%',
          once: true,
        },
      });

      tl.from(sectionRef.current.querySelector('.ss-headline'), {
        opacity: 0,
        y: 16,
        duration: 0.7,
        ease,
      })
        .from(
          sectionRef.current.querySelector('.ss-subhead'),
          { opacity: 0, y: 12, duration: 0.6, ease },
          '-=0.45'
        )
        .from(
          phoneRef.current,
          { opacity: 0, scale: 0.95, y: 20, duration: 0.7, ease },
          '-=0.3'
        )
        .from(
          rowsRef.current,
          { opacity: 0, x: -10, duration: 0.5, stagger: 0.07, ease },
          '-=0.4'
        )
        .from(
          deviceRef.current,
          { opacity: 0, scale: 0.92, rotate: -10, duration: 0.7, ease },
          '-=0.5'
        )
        .from(
          gridRef.current.children,
          { opacity: 0, y: 14, duration: 0.5, stagger: 0.06, ease },
          '-=0.35'
        );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={sectionRef} className="w-full">
      <p className="ss-headline text-3xl sm:text-4xl font-bold tracking-tight text-center mb-4">
        {t('smartSummaries.headline1')}
        <br />
        <span className="text-[var(--accent-link)]">{t('smartSummaries.headline2')}</span>
      </p>
      <p className="ss-subhead text-[var(--text-secondary)] text-center max-w-xl mx-auto mb-14">
        {t('smartSummaries.subhead')}
      </p>

      <div className="relative flex items-center justify-center mb-14" style={{ minHeight: 380 }}>
        <div
          ref={phoneRef}
          className="relative w-[240px] rounded-[32px] p-4 glass-card"
          style={{ transform: 'translateX(-8%)' }}
        >
          <div className="flex items-center justify-between text-[10px] text-[var(--text-secondary)] mb-3 px-1">
            <span>9:41</span>
            <span className="tracking-widest">●●●●</span>
          </div>
          <p className="text-sm font-bold mb-3 px-1">{t('smartSummaries.phoneHeader')}</p>
          <div className="flex flex-col gap-2">
            {RECORDINGS.map((rec, i) => (
              <div
                key={i}
                ref={(el) => (rowsRef.current[i] = el)}
                className="rounded-xl border border-[var(--border-subtle)] bg-white/[0.03] px-3 py-2.5"
              >
                <p className="text-[12px] font-semibold leading-tight mb-1">{rec.title}</p>
                <p className="text-[10px] text-[var(--accent-link)]">
                  ▶ {rec.duration} · {t('smartSummaries.summarized')}
                </p>
              </div>
            ))}
          </div>
        </div>

        <img
          ref={deviceRef}
          src={HERO_PRODUCT_IMG}
          alt="ScribAI"
          className="absolute w-[150px] drop-shadow-2xl"
          style={{ right: '50%', bottom: '-2%', transform: 'translateX(60%) rotate(-4deg)' }}
        />
      </div>

      <div ref={gridRef} className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
        {usecaseLabels.map((label, i) => {
          const Icon = USE_CASE_ICONS[i];
          return (
            <div
              key={i}
              className="flex items-center gap-3.5 rounded-2xl border border-[var(--border-subtle)] bg-white/[0.02] px-4.5 py-4 transition-colors duration-200 hover:border-[var(--accent-link)]/40 hover:bg-white/[0.04]"
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-[11px] bg-[var(--accent-link)]/10 flex items-center justify-center">
                <Icon size={18} className="text-[var(--accent-link)]" />
              </div>
              <span className="text-sm font-semibold">{label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SmartSummariesSection;
